/* KTS Vinyl Collection Value Estimator v2 — Alpine.js component (i18n)
 * Pulls user's Discogs collection, samples for pricing, extrapolates total value,
 * compares against typical home-insurance caps. All client-side, no data leaves browser.
 * UI strings read from window.KTS_VE_I18N (injected by Liquid section per locale).
 */
function _ktsT(k, fallback) {
  return (window.KTS_VE_I18N && window.KTS_VE_I18N[k]) || fallback;
}
function _ktsN(k, fallback) {
  return (window.KTS_VE_NUMBERS_I18N && window.KTS_VE_NUMBERS_I18N[k]) || fallback;
}
// Alpine store for cross-section/snippet result sharing
document.addEventListener('alpine:init', () => {
  if (window.Alpine && typeof window.Alpine.store === 'function') {
    window.Alpine.store('ve', { results: null });
  }
});
function ktsValueEstimator() {
  return {
    username: '',
    currency: 'USD',
    userToken: '',
    showAdvanced: false,
    isLoading: false,
    progress: 0,
    progressText: '',
    error: '',
    results: null,

    init() {
      try {
        const saved = localStorage.getItem('kts_ve_user_token');
        if (saved) { this.userToken = saved; this.showAdvanced = true; }
        const params = new URLSearchParams(window.location.search);
        const u = params.get('u');
        if (u) {
          this.username = u;
          const c = params.get('c'); if (c) this.currency = c;
          setTimeout(() => this.calculate(), 500);
        }
      } catch (e) {}
    },

    saveToken() {
      try {
        if (this.userToken) localStorage.setItem('kts_ve_user_token', this.userToken);
        else localStorage.removeItem('kts_ve_user_token');
      } catch (e) {}
    },

    sleep(ms) { return new Promise(r => setTimeout(r, ms)); },

    async fetchJSON(url) {
      const headers = { 'Accept': 'application/json' };
      if (this.userToken) headers['Authorization'] = 'Discogs token=' + this.userToken;
      const res = await fetch(url, { headers });
      if (res.status === 404) throw new Error(_ktsT('err_404', 'Discogs user not found. Check the username spelling.'));
      if (res.status === 401) throw new Error(_ktsT('err_401', 'Profile is private or your token is invalid.'));
      if (res.status === 403) throw new Error(_ktsT('err_403', 'Discogs blocked the request. Try again later.'));
      if (res.status === 429) throw new Error(_ktsT('err_429', 'Discogs rate limit hit. Add your free Personal Access Token (Advanced) to triple the speed, or wait a minute.'));
      if (!res.ok) throw new Error(_ktsT('err_generic', 'Discogs error ') + res.status);
      return await res.json();
    },

    async fetchCollection(username) {
      this.progressText = _ktsT('progress_fetch', 'Fetching your Discogs collection...');
      this.progress = 5;
      const all = [];
      let page = 1;
      const maxPages = 30;
      let apiTotalItems = 0;
      while (page <= maxPages) {
        const url = 'https://api.discogs.com/users/' + encodeURIComponent(username) +
          '/collection/folders/0/releases?per_page=100&page=' + page;
        const data = await this.fetchJSON(url);
        if (page === 1 && data.pagination) apiTotalItems = data.pagination.items || 0;
        const releases = (data.releases || []).map(r => ({
          id: r.id,
          title: (r.basic_information && r.basic_information.title) || 'Unknown',
          artist: (r.basic_information && r.basic_information.artists && r.basic_information.artists[0] && r.basic_information.artists[0].name) || '',
          year: (r.basic_information && r.basic_information.year) || 0
        }));
        all.push(...releases);
        const totalPages = (data.pagination && data.pagination.pages) || 1;
        if (page >= totalPages) break;
        page++;
        await this.sleep(this.userToken ? 1100 : 2500);
      }
      all._apiTotalItems = apiTotalItems || all.length;
      return all;
    },

    async fetchPrice(releaseId) {
      const key = 'kts_ve_price_' + releaseId + '_' + this.currency;
      try {
        const cached = localStorage.getItem(key);
        if (cached) {
          const obj = JSON.parse(cached);
          if (Date.now() - obj.t < 86400000) return obj.d;
        }
      } catch (e) {}
      const url = 'https://api.discogs.com/releases/' + releaseId + '?curr_abbr=' + this.currency;
      try {
        const data = await this.fetchJSON(url);
        const price = data.lowest_price || 0;
        const have = (data.community && data.community.have) || 1;
        const want = (data.community && data.community.want) || 0;
        const out = { price, have, want };
        try { localStorage.setItem(key, JSON.stringify({ t: Date.now(), d: out })); } catch (e) {}
        return out;
      } catch (e) {
        return null;
      }
    },

    demandMultiplier(want, have) {
      if (!have) return 1.2;
      const r = want / have;
      if (r > 1.0) return 1.8;
      if (r > 0.5) return 1.5;
      if (r > 0.2) return 1.3;
      return 1.15;
    },

    shuffleSample(arr, n) {
      const a = arr.slice();
      for (let i = a.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        const t = a[i]; a[i] = a[j]; a[j] = t;
      }
      return a.slice(0, Math.min(n, a.length));
    },

    computeNumbers(records, totalValue) {
      const hoursRaw = records * 44 / 60;
      const hours = hoursRaw.toFixed(1);
      const days = (hoursRaw / 24).toFixed(1);
      const kg = (records * 0.25).toFixed(1);
      const shelfMRaw = records * 2 / 100;
      const shelfM = shelfMRaw.toFixed(1);
      let recordsCtx;
      if (records < 100) recordsCtx = _ktsN('ctx_starter', 'Just getting started — fun journey ahead.');
      else if (records < 195) recordsCtx = _ktsN('ctx_below', 'Below average. Plenty more to discover.');
      else if (records < 500) recordsCtx = _ktsN('ctx_above', 'Above average. Growing fast.');
      else recordsCtx = _ktsN('ctx_serious', 'Serious collector. Insurance gap matters.');
      const bookcases = Math.max(1, Math.ceil(shelfMRaw / 3.85));
      let shelfCtx;
      if (bookcases === 1) shelfCtx = _ktsN('shelf_one', 'About one bookcase. Standard IKEA Kallax 5×1 fits perfectly.');
      else shelfCtx = _ktsN('shelf_many_pre', 'About ') + bookcases + _ktsN('shelf_many_post', ' bookcases — one IKEA Kallax 5×1 per ≈400 records.');
      const replaceFormatted = this.formatMoney(totalValue);
      return { hours, days, kg, shelfM, recordsCtx, shelfCtx, replaceFormatted };
    },

    async calculate() {
      this.error = '';
      this.results = null;
      const u = this.username.trim();
      if (!u) { this.error = _ktsT('err_no_username', 'Enter your Discogs username first.'); return; }
      this.isLoading = true;
      this.progress = 0;

      try {
        const collection = await this.fetchCollection(u);
        if (collection.length === 0) {
          this.error = _ktsT('err_empty', 'Your Discogs collection is empty or your profile is private.');
          this.isLoading = false;
          return;
        }
        const total = collection.length;
        const maxSample = this.userToken ? 50 : 20;
        const sampleSize = total <= maxSample ? total : maxSample;
        const sample = total === sampleSize ? collection : this.shuffleSample(collection, sampleSize);

        const priced = [];
        for (let i = 0; i < sample.length; i++) {
          this.progress = 10 + Math.round((i / sample.length) * 80);
          this.progressText = _ktsT('progress_price_pre', 'Pricing record ') + (i+1) + _ktsT('progress_price_of', ' of ') + sample.length + _ktsT('progress_price_post', '...');
          const p = await this.fetchPrice(sample[i].id);
          if (p && p.price > 0) {
            const est = p.price * this.demandMultiplier(p.want, p.have);
            priced.push(Object.assign({}, sample[i], { lowest: p.price, want: p.want, have: p.have, value: est }));
          }
          await this.sleep(this.userToken ? 1100 : 2500);
        }

        this.progress = 95;
        this.progressText = _ktsT('progress_calc', 'Calculating totals...');

        const realTotal = collection._apiTotalItems || total;

        const valsAsc = priced.map(r => r.value).sort((a, b) => a - b);
        let medianVal = 0;
        if (valsAsc.length) {
          const mid = Math.floor(valsAsc.length / 2);
          medianVal = valsAsc.length % 2 ? valsAsc[mid] : (valsAsc[mid - 1] + valsAsc[mid]) / 2;
        }
        const sampleSum = priced.reduce((s, r) => s + r.value, 0);
        const meanVal = priced.length ? sampleSum / priced.length : 0;
        const totalValueLow = medianVal * realTotal;
        const totalValueHigh = meanVal * realTotal;
        const totalValue = Math.round(Math.sqrt(Math.max(1, totalValueLow * totalValueHigh)));
        const top10 = priced.slice().sort((a, b) => b.value - a.value).slice(0, 10);

        const thresholds = {
          USD: [1000, 2500, 5000], EUR: [1000, 2500, 5000],
          GBP: [1000, 2000, 4000], AUD: [1500, 3500, 7000], CAD: [1500, 3500, 7000]
        };
        const caps = thresholds[this.currency] || thresholds.USD;
        let gapLevel = 'covered';
        if (totalValue > caps[2]) gapLevel = 'critical';
        else if (totalValue > caps[1]) gapLevel = 'high';
        else if (totalValue > caps[0]) gapLevel = 'moderate';

        this.results = {
          username: u, totalRecords: realTotal, sampleSize: priced.length,
          requestedSample: sample.length, sampleAvg: medianVal, totalValue,
          totalValueLow: Math.round(totalValueLow), totalValueHigh: Math.round(totalValueHigh),
          currency: this.currency, top10, gapLevel, caps,
          numbers: this.computeNumbers(realTotal, totalValue)
        };
        // Publish to Alpine store so the snippet rendered outside this section can read it
        try { if (window.Alpine && window.Alpine.store('ve')) window.Alpine.store('ve').results = this.results; } catch (e) {}
        this.progress = 100;
        this.progressText = _ktsT('progress_done', 'Done.');

        try {
          const newUrl = new URL(window.location.href);
          newUrl.searchParams.set('u', u);
          newUrl.searchParams.set('c', this.currency);
          window.history.replaceState({}, '', newUrl);
        } catch (e) {}
      } catch (e) {
        this.error = e.message || _ktsT('err_default', 'Something went wrong. Try again.');
      } finally {
        this.isLoading = false;
      }
    },

    formatMoney(n) {
      if (n == null) return '—';
      const sym = { USD: '$', EUR: '€', GBP: '£', AUD: 'A$', CAD: 'C$' }[this.currency] || '$';
      return sym + Math.round(n).toLocaleString('en-US');
    },

    shareUrl() {
      const url = window.location.href;
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(url).then(() => alert(_ktsT('share_copied', 'Result URL copied. Paste anywhere to share.')));
      } else {
        window.prompt(_ktsT('share_prompt', 'Copy this URL to share:'), url);
      }
    },

    exportPDF() {
      if (!this.results || !window.jspdf) return;
      const { jsPDF } = window.jspdf;
      const r = this.results;
      const doc = new jsPDF({ unit: 'mm', format: 'a4' });
      const sym = { USD: '$', EUR: '€', GBP: '£', AUD: 'A$', CAD: 'C$' }[this.currency] || '$';

      doc.setFillColor(26, 26, 26);
      doc.rect(0, 0, 210, 40, 'F');
      doc.setTextColor(255, 189, 0);
      doc.setFontSize(11);
      doc.text('KEEP THEM SPINNING', 15, 18);
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(18);
      doc.text('Vinyl Collection Value Estimate', 15, 30);

      doc.setTextColor(100, 100, 100);
      doc.setFontSize(9);
      doc.text('Discogs username: ' + r.username, 15, 50);
      doc.text('Generated: ' + new Date().toLocaleDateString(), 15, 56);

      doc.setFillColor(255, 248, 225);
      doc.rect(15, 65, 180, 35, 'F');
      doc.setTextColor(153, 107, 0);
      doc.setFontSize(10);
      doc.text('ESTIMATED MARKET VALUE', 22, 75);
      doc.setTextColor(26, 26, 26);
      doc.setFontSize(28);
      doc.text(sym + Math.round(r.totalValue).toLocaleString('en-US'), 22, 90);
      doc.setTextColor(100, 100, 100);
      doc.setFontSize(9);
      doc.text('Range: ' + sym + r.totalValueLow.toLocaleString('en-US') + ' - ' + sym + r.totalValueHigh.toLocaleString('en-US'), 22, 96);

      doc.setTextColor(26, 26, 26);
      doc.setFontSize(11);
      doc.text('Stats', 15, 115);
      doc.setFontSize(10);
      doc.setTextColor(100, 100, 100);
      doc.text('Records in collection: ' + r.totalRecords, 15, 124);
      doc.text('Median value per record: ' + sym + Math.round(r.sampleAvg), 15, 131);
      doc.text('Sample priced: ' + r.sampleSize + ' of ' + r.requestedSample, 15, 138);

      doc.setTextColor(26, 26, 26);
      doc.setFontSize(11);
      doc.text('Top valued records', 15, 152);
      doc.setFontSize(9);
      let y = 160;
      (r.top10 || []).slice(0, 8).forEach((rec, i) => {
        if (y > 270) return;
        doc.setTextColor(26, 26, 26);
        const title = (rec.artist + ' - ' + rec.title).substring(0, 70);
        doc.text((i+1) + '. ' + title, 15, y);
        doc.setTextColor(100, 100, 100);
        doc.text(sym + Math.round(rec.value), 175, y, { align: 'right' });
        y += 6;
      });

      doc.setFillColor(26, 26, 26);
      doc.rect(0, 282, 210, 15, 'F');
      doc.setTextColor(255, 189, 0);
      doc.setFontSize(8);
      doc.text('keepthemspinning.com/pages/vinyl-collection-value-estimator', 15, 291);
      doc.setTextColor(180, 180, 180);
      doc.text('Estimate based on Discogs marketplace data. Not a professional appraisal.', 15, 295);

      doc.save('kts-vinyl-collection-value-' + r.username + '.pdf');
    }
  };
}
